import React, { FC } from 'react';
import { Box, Heading, Text, Button, Image } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import s from './MobileSitePreview.scss';
import { useDsUserInsights } from '../../../context/dsUserInsights';
import { CategoryKey } from '@common/constants/categories';
import { useCategoryImage } from '../hooks/useCategoryImage';
import { useUpgradePlan } from '../hooks/useUpgradePlan';
import { useViewSite } from '../hooks/useViewSite';
import { useQuerySitesUrl } from '../context/querySitesContext';

import { Footer } from '../../../components/Footer';
import { SiteIframe } from '../components/SiteIframe/SiteIframe';
import { useSpec } from '../../../hooks/useSpec';
import { Off50YearlyOffer } from '../../../experiments';
import { PromotionalOffer } from '../components/PromotionalOffer';
import { useCurrentCategoryValue } from '../../../context';

export const MobileSitePreview: FC = () => {
  const { t } = useTranslation();
  const selectedCategory = useCurrentCategoryValue();
  const { image: categoryImage, isLoading: isImageLoading } = useCategoryImage(selectedCategory as CategoryKey);
  const userInsights = useDsUserInsights();
  const { upgradePlan, isLoading: isUpgradeLoading } = useUpgradePlan();
  const { viewLiveSite, isViewLoading } = useViewSite();
  const isOff50YearlyOffer = useSpec(Off50YearlyOffer);
  const siteUrl = useQuerySitesUrl();
  const currentStoreName = userInsights?.storeName?.summary;

  const handleCopyCode = async (couponCode: string) => {
    await navigator.clipboard.writeText(couponCode);
  };

  const handleUpgradeClick = () => {
    upgradePlan();
  };

  const renderContent = () => {
    if (siteUrl) {
      return <SiteIframe storeName={currentStoreName} src={siteUrl} />;
    }

    return (
      <Box className={s.imageWrapper}>
        {isImageLoading ? (
          <Box align="center" verticalAlign="middle" height="100%">
            <Text>{t('LOADING_PREVIEW')}</Text>
          </Box>
        ) : (
          <Image src={categoryImage!} alt="Site Preview" />
        )}
      </Box>
    );
  };

  const renderFooter = () => {
    return (
      <Footer
        isLoading={isUpgradeLoading || isViewLoading}
        onClick={handleUpgradeClick}
        nextButtonText={t('UPGRADE_NOW')}
        className={s.templateFooter}
        skin="premium"
        button={
          isOff50YearlyOffer && (
            <PromotionalOffer
              variant="button"
              onGetOffer={upgradePlan}
              onCopyCode={handleCopyCode}
              disabled={isUpgradeLoading}
            />
          )
        }
      />
    );
  };

  return (
    <Box className={s.container} direction="vertical">
      <Box className={s.mobileLayout} direction="vertical">
        <Box className={s.mobileContent} direction="vertical" gap={4}>
          <Box className={s.headerTextWrapper} direction="vertical" gap={1}>
            <Heading className={s.title} size="medium">
              {`✨ ${t('CONGRATS')}! ✨`}
            </Heading>

            <Text>{t('YOUR_SITE_NOW_LIVE')}!</Text>

            <Text className={s.textDisclaimer}>
              {t('IN_ORDER_TO_START_SELLING_ONLINE_YOU_JUST_NEED_TO_UPGRADE')}
            </Text>
          </Box>

          {isOff50YearlyOffer && <PromotionalOffer variant="mobile" onCopyCode={handleCopyCode} />}

          <Box className={s.sitePreviewCard} direction="vertical">
            <Box className={s.liveBadge} align="center" gap={2}>
              <Box className={s.liveDot} />
              <Text className={s.liveText}>Live on the Web</Text>
            </Box>

            <Box className={s.previewContent} direction="vertical" gap={2}>
              <Box direction="vertical" gap={1}>
                <Heading size="small">{currentStoreName}</Heading>
              </Box>

              {renderContent()}
            </Box>
          </Box>

          <Box className={s.plansWrapper}>
            <Box direction="vertical" gap={1}>
              <Text>{t('CURRENT_PLAN')}</Text>
              <Text weight="bold">{t('FREE_PLAN')}</Text>
            </Box>

            <Button
              size="small"
              skin="premium"
              className={s.upgradeButton}
              onClick={handleUpgradeClick}
              disabled={isUpgradeLoading}
            >
              {t('UPGRADE_NOW')}
            </Button>
          </Box>
        </Box>

        {renderFooter()}
      </Box>
    </Box>
  );
};
